function formatShippingTime(shippingStart, shippingEnd, backorderDate) {
  if (!window.ShippingEstimate) {
    return "";
  }

  return window.ShippingEstimate.formatText({
    shipDateBegin: shippingStart,
    shipDateEnd: shippingEnd,
    backorderDate: backorderDate,
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.setAttribute("aria-labelledby", "Cart");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, {elementToFocus: focusElement});
      },
      {once: true},
    );

    document.body.classList.add("overflow-hidden");
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.renderCart(parsedState);
    setTimeout(() => {
      this.open();
      this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    });
  }

  renderCart(parsedState) {
    this.classList.contains("is-empty") && this.classList.remove("is-empty");
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Re-initialize cart drawer sections after render
    if (typeof initCartDrawerSections === "function") {
      initCartDrawerSections();
    }

    if (typeof CrossSellCardImageSync !== "undefined") {
      CrossSellCardImageSync.syncCartDrawer();
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", {bubbles: true});
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.input.addEventListener("input", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

class CartDrawerUpsellItem extends HTMLElement {
  constructor() {
    super();
    // Support both old UI (icon button) and new cross-sell style UI (text button)
    this.button =
      this.querySelector("[data-upsell-add-to-cart]") ||
      this.querySelector(".cart-drawer__upsell-add-btn:not(.cart-drawer__upsell-add-btn--select)");
    this.chooseOptionBtn = this.querySelector("[data-upsell-choose-option]");
    this.isAdding = false;
    this.modal = document.getElementById("quick-view-modal");
    this.content = document.getElementById("quick-view-content");
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener("click", this.handleClick.bind(this));
    }
    if (this.chooseOptionBtn) {
      this.chooseOptionBtn.addEventListener("click", this.handleChooseOption.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener("click", this.handleClick.bind(this));
    }
    if (this.chooseOptionBtn) {
      this.chooseOptionBtn.removeEventListener("click", this.handleChooseOption.bind(this));
    }
  }

  /**
   * Handle "Choose Option" button click - opens quick view modal
   */
  async handleChooseOption(e) {
    e.preventDefault();

    const productHandle = this.chooseOptionBtn.getAttribute("data-product-handle");
    if (!productHandle) return;

    this.chooseOptionBtn.classList.add("is-adding");
    const originalText = this.chooseOptionBtn.textContent;
    this.chooseOptionBtn.textContent = "Loading...";

    try {
      const response = await fetch(`/products/${productHandle}?view=quick-view`);
      const html = await response.text();

      const newEl = document.createElement("div");
      newEl.classList.add("quick-view-content");
      newEl.innerHTML = html;

      const quickViewSection = newEl.querySelector("#shopify-section-product-quick-view");
      if (quickViewSection && this.content) {
        this.content.innerHTML = quickViewSection.innerHTML;
      } else if (this.content) {
        this.content.innerHTML = newEl.innerHTML;
      }

      if (this.modal) {
        this.modal.classList.remove("hidden");
        document.body.classList.add("overflow-hidden");
      }

      // Initialize quick view logic with cart drawer upsell context
      this.initQuickViewForCartUpsell();
    } catch (err) {
      console.error("Error loading quick view:", err);
    } finally {
      this.chooseOptionBtn.classList.remove("is-adding");
      this.chooseOptionBtn.textContent = originalText;
    }
  }

  /**
   * Initialize quick view modal for cart drawer upsell context
   */
  initQuickViewForCartUpsell() {
    const productInfoEl = document.querySelector("#quick-view-content #product-info");
    const form = document.getElementById("quick-view-form");
    const viewDetailsLink = document.querySelector("#quick-view-content .view-details");
    if (viewDetailsLink) {
      viewDetailsLink.style.display = "none";
    }

    if (!productInfoEl) return;

    const product = JSON.parse(productInfoEl.innerHTML);

    // Image cleanup (since content is injected)
    const mainImage = document.querySelector("#quick-view-image img");
    const mainImageSources = document.querySelectorAll("#quick-view-image source");
    for (const imageSource of mainImageSources) imageSource.remove();
    if (mainImage) {
      mainImage.removeAttribute("srcset");
      mainImage.removeAttribute("data-srcset");
      mainImage.removeAttribute("data-src");
    }

    // Get variants data for shipping info
    let variantsData = {};
    const variantsDataEl = document.querySelector("#quick-view-content [data-variants-data-json]");
    if (variantsDataEl) {
      try {
        variantsData = JSON.parse(variantsDataEl.innerHTML);
      } catch (e) {
        console.error("Error parsing variants data:", e);
      }
    }

    if (!form) {
      // Simple product without variants
      const simpleForm = document.querySelector("#quick-view-content .recommended-product-form");
      if (simpleForm) {
        const submitBtn = simpleForm.querySelector('button[type="submit"]');
        if (submitBtn) {
          submitBtn.textContent = "Select Add On";
          submitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            this.handleQuickViewSubmit(product.variants[0], variantsData);
          });
        }
      }
      return;
    }

    // Quick add button behavior for variant products
    const quickAddBtn = form.querySelector("[data-quick-add-btn]");
    if (quickAddBtn) {
      // Mark as cart upsell mode
      quickAddBtn.dataset.cartUpsellMode = "true";

      quickAddBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const variantIdInput = document.getElementById("selected-variant-id");
        const currentVariant = product.variants.find((v) => v.id == variantIdInput?.value);
        if (currentVariant) {
          this.handleQuickViewSubmit(currentVariant, variantsData);
        }
      });
    }

    // Setup variant option change handlers
    this.attachOptionChangeHandlers(form, product);

    // Setup modal close handlers
    this.setupModalCloseHandlers();
  }

  /**
   * Attach option change handlers for variant selection
   */
  attachOptionChangeHandlers(form, product) {
    const optionInputs = form.querySelectorAll('.option-group input[type="radio"]');
    const variantIdInput = document.getElementById("selected-variant-id");
    const quickAddBtn = form.querySelector("[data-quick-add-btn]");
    const unavailableMsg = document.getElementById("quick-view-unavailable-msg");

    const getSelections = () => {
      const sel = [];
      form.querySelectorAll(".option-group").forEach((group, idx) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        sel[idx] = checked ? checked.value.trim().toLowerCase() : null;
      });
      return sel;
    };

    const findMatchingVariant = (selections) => {
      return product.variants.find((v) => {
        return v.options.every((opt, i) => {
          return selections[i] === null || opt.trim().toLowerCase() === selections[i];
        });
      });
    };

    const updateUI = () => {
      const selections = getSelections();
      const allOptionsSelected = selections.every((sel) => sel !== null);
      const matchedVariant = allOptionsSelected ? findMatchingVariant(selections) : null;

      // Update selected value displays
      form.querySelectorAll(".option-group").forEach((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const selectedValueEl = group.querySelector(".selected-value");
        if (selectedValueEl && checked) {
          selectedValueEl.textContent = checked.value;
        }
      });

      // Update variant labels (checked state)
      optionInputs.forEach((input) => {
        const label = input.closest("label");
        if (label) {
          if (input.checked) {
            label.classList.add("is-selected");
          } else {
            label.classList.remove("is-selected");
          }
        }
      });

      const isCartUpsellMode = quickAddBtn?.dataset.cartUpsellMode === "true";

      if (matchedVariant && matchedVariant.available) {
        if (variantIdInput) variantIdInput.value = matchedVariant.id;
        if (quickAddBtn) {
          quickAddBtn.disabled = false;
          quickAddBtn.textContent = isCartUpsellMode ? "Select Add On" : "Add to Cart";
        }
        if (unavailableMsg) unavailableMsg.classList.add("hide");

        // Update price
        this.updateQuickViewPrice(matchedVariant);

        // Update image
        this.updateQuickViewImage(matchedVariant);
      } else if (matchedVariant && !matchedVariant.available) {
        if (variantIdInput) variantIdInput.value = matchedVariant.id;
        if (quickAddBtn) {
          quickAddBtn.disabled = true;
          quickAddBtn.textContent = "Sold Out";
        }
        if (unavailableMsg) unavailableMsg.classList.remove("hide");
      } else {
        if (quickAddBtn) {
          quickAddBtn.disabled = true;
          quickAddBtn.textContent = "Make a Selection";
        }
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener("change", updateUI);
    });

    // Initial UI update
    updateUI();
  }

  /**
   * Update price display in quick view
   */
  updateQuickViewPrice(variant) {
    const priceEl = document.querySelector("#quick-view-content [data-qv-product-price]");
    const comparePriceEl = document.querySelector("#quick-view-content [data-qv-compare-price]");
    const b2bEl = document.querySelector("#quick-view-content .quick-view__price[data-b2b-pricing]");
    let variantsData = null;
    const vdj = document.querySelector("#quick-view-content [data-variants-data-json]");
    if (vdj) {
      try {
        variantsData = JSON.parse(vdj.textContent);
      } catch (e) {
        /* ignore */
      }
    }

    if (priceEl && variant.price) {
      priceEl.textContent = "$" + (variant.price / 100).toFixed(2);
    }

    if (comparePriceEl) {
      let compareCents = null;
      if (b2bEl && typeof ProductVariantUtils !== "undefined" && ProductVariantUtils.getQuickViewB2BCompareCents) {
        compareCents = ProductVariantUtils.getQuickViewB2BCompareCents(variant, variantsData && variantsData[variant.id]);
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareCents = variant.compare_at_price;
      }
      if (compareCents != null && compareCents > 0) {
        comparePriceEl.textContent = "$" + (compareCents / 100).toFixed(2);
        comparePriceEl.style.display = "";
      } else {
        comparePriceEl.textContent = "";
        comparePriceEl.style.display = "none";
      }
    }
  }

  /**
   * Update image in quick view
   */
  updateQuickViewImage(variant) {
    if (!variant.featured_image) return;

    const mainImage = document.querySelector("#quick-view-image img");
    if (mainImage) {
      mainImage.src = variant.featured_image.src;
    }
  }

  /**
   * Handle quick view form submission for cart upsell
   */
  async handleQuickViewSubmit(variant, variantsData) {
    if (!variant) return;

    // Get shipping data from variant metafields
    const variantData = variantsData[variant.id] || {};
    const shippingData = variantData.variant_shipping || {};

    const shipDateBegin = parseInt(shippingData.ship_date_begin) || 0;
    const shipDateEnd = parseInt(shippingData.ship_date_end) || 0;
    const backorderDate = shippingData.backorder_date || "";

    // Close modal first
    this.closeQuickView();

    // Now add the upsell to cart using existing logic
    this.isAdding = true;

    const baseProduct = this.dataset.baseProduct;
    const baseVariantId = this.dataset.baseVariantId;
    const baseItemKey = this.dataset.baseItemKey;
    const baseQuantity = parseInt(this.dataset.baseQuantity) || 1;

    // Parse base item properties
    let baseProperties = {};
    try {
      baseProperties = JSON.parse(this.dataset.baseProperties || "{}");
    } catch (err) {
      console.error("Error parsing base properties:", err);
    }

    // Generate a shared timestamp for the bundle
    const timestamp = Math.floor(Date.now()).toString();

    // Get base item shipping data
    const baseShippingStart = parseInt(this.dataset.shippingStart) || 0;
    const baseShippingEnd = parseInt(this.dataset.shippingEnd) || 0;
    const baseBackorderDate = this.dataset.backorderDate || "";

    // Use the later shipping dates
    const finalShippingStart = Math.max(baseShippingStart, shipDateBegin);
    const finalShippingEnd = Math.max(baseShippingEnd, shipDateEnd);

    // Compare backorder dates and use the later one
    let finalBackorderDate = baseBackorderDate;
    if (backorderDate) {
      const baseBackorderSeconds = baseBackorderDate ? new Date(baseBackorderDate).getTime() : 0;
      const upsellBackorderSeconds = new Date(backorderDate).getTime();
      if (upsellBackorderSeconds > baseBackorderSeconds) {
        finalBackorderDate = backorderDate;
      }
    }

    try {
      // Remove the original base item from cart
      const removeResponse = await fetch("/cart/change.js", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({id: baseItemKey, quantity: 0}),
      });

      if (!removeResponse.ok) {
        throw new Error("Failed to remove base item from cart");
      }

      // Add both base product and upsell product with bundle properties
      const baseItemProperties = {
        ...baseProperties,
        _grouped_id: baseProduct,
        _type: "base",
        _ts: timestamp,
        "Shipping Time": formatShippingTime(finalShippingStart, finalShippingEnd, finalBackorderDate),
      };

      const formData = {
        items: [
          {
            id: parseInt(baseVariantId),
            quantity: baseQuantity,
            properties: baseItemProperties,
          },
          {
            id: parseInt(variant.id),
            quantity: 1,
            properties: Object.assign(
              {
                "Bundled With": baseProduct,
                _grouped_id: baseProduct,
                _type: "bundle",
                _ts: timestamp,
                "Shipping Time": formatShippingTime(finalShippingStart, finalShippingEnd, finalBackorderDate),
              },
              window.theme && window.theme.isB2B ? {"_Order Type": "B2B"} : {},
            ),
          },
        ],
      };

      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(formData),
      });

      if (addResponse.ok) {
        await this.refreshCartDrawer();
      }
    } catch (error) {
      console.error("Error adding upsell to cart:", error);
      await this.refreshCartDrawer();
    } finally {
      this.isAdding = false;
    }
  }

  /**
   * Setup modal close handlers
   */
  setupModalCloseHandlers() {
    const closeBtn = document.getElementById("close-quick-view");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.closeQuickView());
    }

    const overlay = document.querySelector(".modal-content__overlay");
    if (overlay) {
      overlay.addEventListener("click", () => this.closeQuickView());
    }
  }

  /**
   * Close the quick view modal
   */
  closeQuickView() {
    if (this.modal) {
      this.modal.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
    }
  }

  async handleClick(e) {
    e.preventDefault();

    if (this.isAdding) return;
    this.isAdding = true;
    this.button.classList.add("is-adding");
    this.originalButtonText = this.button.innerHTML;
    this.button.innerHTML = "Adding...";

    const upsellVariantId = this.dataset.upsellVariantId;
    const baseProduct = this.dataset.baseProduct;
    const baseVariantId = this.dataset.baseVariantId;
    const baseItemKey = this.dataset.baseItemKey;
    const baseQuantity = parseInt(this.dataset.baseQuantity) || 1;

    // Parse base item properties (non-hidden ones)
    let baseProperties = {};
    try {
      baseProperties = JSON.parse(this.dataset.baseProperties || "{}");
    } catch (err) {
      console.error("Error parsing base properties:", err);
    }

    // Generate a shared timestamp for the bundle
    const timestamp = Math.floor(Date.now()).toString();

    try {
      // Step 1: Remove the original base item from cart
      const removeResponse = await fetch("/cart/change.js", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({id: baseItemKey, quantity: 0}),
      });

      if (!removeResponse.ok) {
        throw new Error("Failed to remove base item from cart");
      }

      const shippingStart = parseInt(this.dataset.shippingStart) || 0;
      const shippingEnd = parseInt(this.dataset.shippingEnd) || 0;
      const backorderDate = this.dataset.backorderDate || "";

      // Step 2: Add both base product and upsell product with bundle properties
      const baseItemProperties = {
        ...baseProperties,
        _grouped_id: baseProduct,
        _type: "base",
        _ts: timestamp,
        "Shipping Time": formatShippingTime(shippingStart, shippingEnd, backorderDate),
      };

      const formData = {
        items: [
          {
            id: parseInt(baseVariantId),
            quantity: baseQuantity,
            properties: baseItemProperties,
          },
          {
            id: parseInt(upsellVariantId),
            quantity: 1,
            properties: Object.assign(
              {
                "Bundled With": baseProduct,
                _grouped_id: baseProduct,
                _type: "bundle",
                _ts: timestamp,
                "Shipping Time": formatShippingTime(shippingStart, shippingEnd),
              },
              window.theme && window.theme.isB2B ? {"_Order Type": "B2B"} : {},
            ),
          },
        ],
      };

      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(formData),
      });

      if (addResponse.ok) {
        await this.refreshCartDrawer();
      }
    } catch (error) {
      console.error("Error adding upsell to cart:", error);
      await this.refreshCartDrawer();
    } finally {
      this.isAdding = false;
      if (this.button) {
        this.button.classList.remove("is-adding");
        if (this.originalButtonText) {
          this.button.innerHTML = this.originalButtonText;
        }
      }
    }
  }

  async refreshCartDrawer() {
    try {
      const sectionsResponse = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await sectionsResponse.json();

      if (sections["cart-drawer"]) {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(sections["cart-drawer"], "text/html");
        const newDrawerInner = newDoc.querySelector(".drawer__inner");
        const currentDrawerInner = document.querySelector("#CartDrawer .drawer__inner");

        if (newDrawerInner && currentDrawerInner) {
          currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }

        const cartDrawer = document.querySelector("cart-drawer");
        const newCartDrawer = newDoc.querySelector("cart-drawer");
        if (cartDrawer && newCartDrawer) {
          const isEmpty = newCartDrawer.classList.contains("is-empty");
          cartDrawer.classList.toggle("is-empty", isEmpty);
          if (isEmpty) {
            cartDrawer.classList.remove("drawer__close");
          }
        }

        // Re-initialize upsell and cross-sell sections after cart refresh
        if (typeof initCartDrawerSections === "function") {
          initCartDrawerSections();
        }

        if (typeof CrossSellCardImageSync !== "undefined") {
          CrossSellCardImageSync.syncCartDrawer();
        }
      }

      if (sections["cart-icon-bubble"]) {
        const cartIconBubble = document.getElementById("cart-icon-bubble");
        if (cartIconBubble) {
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(sections["cart-icon-bubble"], "text/html");
          const newBubble = newDoc.querySelector(".shopify-section");
          if (newBubble) {
            cartIconBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }
    } catch (error) {
      console.error("Error refreshing cart drawer:", error);
    }
  }
}

customElements.define("cart-drawer-upsell-item", CartDrawerUpsellItem);

// Custom element for upsell products section with accordion functionality
class UpSellCartProducts extends HTMLElement {
  connectedCallback() {
    this.handleAccordions();
  }
  handleAccordions() {
    const accordionTabs = this.querySelectorAll(".cart-drawer__cross-sell__tab");
    const accordionContents = this.querySelectorAll(".cart-drawer__cross-sell__content");

    accordionTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const tabTitle = tab.getAttribute("data-title");

        tab.classList.toggle("active");

        accordionContents.forEach((content) => {
          const contentTitle = content.getAttribute("data-title");
          if (contentTitle === tabTitle) {
            content.classList.toggle("active");
          }
        });
      });
    });
  }
}

customElements.define("up-sell-cart-products", UpSellCartProducts);
// Initialize quick view modal variant selection and form handling
function initQuickViewModal() {
  const productInfoEl = document.querySelector("#quick-view-content #product-info");
  const form = document.getElementById("quick-view-form");
  const modal = document.getElementById("quick-view-modal");

  if (!productInfoEl || !form) return;

  const product = JSON.parse(productInfoEl.innerHTML);
  const variantIdInput = document.getElementById("selected-variant-id");
  const mainImage = document.querySelector("#quick-view-image img");
  const mainImageSources = document.querySelectorAll("#quick-view-image source");

  // Image cleanup (since content is injected)
  for (const imageSource of mainImageSources) imageSource.remove();
  if (mainImage) {
    mainImage.removeAttribute("srcset");
    mainImage.removeAttribute("data-srcset");
    mainImage.removeAttribute("data-src");
  }

  // Helper function to get current selections
  function getSelections() {
    const sel = [];
    form.querySelectorAll(".option-group").forEach(function (group, idx) {
      const checked = group.querySelector('input[type="radio"]:checked');
      sel[idx] = checked ? checked.value.trim().toLowerCase() : null;
    });
    return sel;
  }

  // Helper function to find matching variant
  function findMatchingVariant(selections) {
    return product.variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return selections[i] === null || opt.trim().toLowerCase() === selections[i];
      });
    });
  }

  // Update UI based on selection
  function updateUIForSelection() {
    const sel = getSelections();
    const totalOptions = product.options?.length || 1;
    const fullPicked = sel.filter(Boolean).length === totalOptions;

    const exact = fullPicked ? findMatchingVariant(sel) : null;
    const btn = form.querySelector("[data-quick-add-btn]");
    const msg = document.querySelector("#quick-view-unavailable-msg");

    if (fullPicked) {
      if (exact && exact.available) {
        // Enable add to cart
        if (btn) {
          btn.disabled = false;
          btn.textContent = "Add to Cart";
        }
        if (msg) msg.classList.add("hide");
        if (variantIdInput) variantIdInput.value = exact.id;

        // Update price
        const priceEl = document.querySelector("#quick-view-content [data-qv-product-price]");
        const comparePriceEl = document.querySelector("#quick-view-content [data-qv-compare-price]");
        if (priceEl) {
          const formatMoney = function (cents) {
            return "$" + (Number(cents) / 100).toFixed(2);
          };
          priceEl.textContent = formatMoney(exact.price);
          if (comparePriceEl) {
            const b2bEl = document.querySelector("#quick-view-content .quick-view__price[data-b2b-pricing]");
            let variantsData = null;
            const vdj = document.querySelector("#quick-view-content [data-variants-data-json]");
            if (vdj) {
              try {
                variantsData = JSON.parse(vdj.textContent);
              } catch (e) {
                /* ignore */
              }
            }
            let compareCents = null;
            if (b2bEl && typeof ProductVariantUtils !== "undefined" && ProductVariantUtils.getQuickViewB2BCompareCents) {
              compareCents = ProductVariantUtils.getQuickViewB2BCompareCents(exact, variantsData && variantsData[exact.id]);
            } else if (exact.compare_at_price && exact.compare_at_price > exact.price) {
              compareCents = exact.compare_at_price;
            }
            if (compareCents != null && compareCents > 0) {
              comparePriceEl.textContent = formatMoney(compareCents);
              comparePriceEl.style.display = "";
            } else {
              comparePriceEl.textContent = "";
              comparePriceEl.style.display = "none";
            }
          }
        }

        // Update image
        if (mainImage && exact.featured_image?.src) {
          mainImage.setAttribute("src", exact.featured_image.src);
          mainImage.setAttribute("srcset", "");
        }

        // Update line item properties
        if (typeof VendorNotesUtils !== "undefined") {
          VendorNotesUtils.updateLineItemProperties(exact);
        }
      } else {
        // Selection unavailable
        if (btn) {
          btn.disabled = true;
          btn.textContent = "Unavailable";
        }
        if (msg) msg.classList.remove("hide");
        if (variantIdInput && exact) variantIdInput.value = exact.id;
        if (mainImage && exact?.featured_image?.src) {
          mainImage.setAttribute("src", exact.featured_image.src);
          mainImage.setAttribute("srcset", "");
        }
      }
    }
  }

  // Bind radio button changes
  form.querySelectorAll('input[type="radio"]').forEach(function (radio) {
    radio.addEventListener("change", function () {
      // Update "selected value" text
      const selectedValueEl = radio.closest(".option-group")?.querySelector(".option-group__title .selected-value");
      if (selectedValueEl) {
        selectedValueEl.textContent = radio.value;
      }

      // Visual "checked" state
      radio
        .closest(".option-group__values")
        ?.querySelectorAll("label.variant-label")
        .forEach(function (l) {
          l.classList.remove("checked");
        });
      radio.closest("label")?.classList.add("checked");

      updateUIForSelection();
    });
  });

  // Make labels clickable (trigger radio change)
  form.querySelectorAll("label.variant-label").forEach(function (label) {
    label.addEventListener("click", function (e) {
      const radio = label.querySelector('input[type="radio"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change", {bubbles: true}));
      }
    });
  });

  // Quick add button behavior
  const quickAddBtn = form.querySelector("[data-quick-add-btn]");
  if (quickAddBtn) {
    quickAddBtn.addEventListener("click", function (e) {
      e.preventDefault();
      if (typeof CartUtils !== "undefined") {
        const cart = document.querySelector("cart-drawer");
        CartUtils.submitForm(form, quickAddBtn, cart);
        if (modal) {
          modal.classList.add("hidden");
          document.body.classList.remove("overflow-hidden");
        }
      }
    });
  }

  // Initial UI update
  updateUIForSelection();
}
